import { F } from "@mobily/ts-belt";
import clsx from "clsx";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import SignTypeBadge from "../../../components/SignTypeBadge/index.jsx";
import { SignType } from "../../../domain/index.js";
import { useMediaQuery } from "../../../hooks/index.js";
import ProductThumbnail from "../../../components/ProductThumbnail/ProductThumbnail";
import { usd } from "../../../util/formatCurrency.js";
import { joinEnglishPick } from "../../../util/text.js";
import s from "./ProductCard.module.css";
import { isSoldOut } from "./ProductPage/productAdapter.js";

const getAvailability = (product) => {
  const soldOut = isSoldOut(product);
  const isVaulted =
    !soldOut &&
    product.vaulted_until &&
    new Date(product.vaulted_until) > new Date();
  return { soldOut, isVaulted, unavailable: soldOut || isVaulted };
};

const buildAriaLabel = ({ name, priceStr, soldOut, isVaulted }) =>
  soldOut
    ? `${name}, ${priceStr}, Sold Out`
    : isVaulted
    ? `${name}, Vaulted`
    : `${name}, ${priceStr}`;

const pickBestSignedType = (product) =>
  product._variants?.reduce((v, cur) => (cur.price > v.price ? cur : v))
    ?.signed_type;

const buildImgClass = (isSquare, soldOut, isVaulted) =>
  [
    isSquare ? s.cardImg : s.cardImgInset,
    soldOut ? s.imgGrayscale : isVaulted ? s.imgDesaturate : "",
  ]
    .filter(Boolean)
    .join(" ");

function CardImage({ product, soldOut, isVaulted, hasVariants, isSquare }) {
  const signedType = !hasVariants
    ? product.signed_type
    : pickBestSignedType(product);

  const className = buildImgClass(isSquare, soldOut, isVaulted);

  return (
    <ProductThumbnail
      src={product.image_url}
      alt={product.name}
      inset={!isSquare}
      className={className}
    >
      {soldOut && (
        <div className={`${s.overlay} ${s.overlaySoldOut}`}>
          <span className={s.overlayTextSoldOut}>Sold Out</span>
        </div>
      )}
      {isVaulted && (
        <div className={`${s.overlay} ${s.overlayVaulted}`}>
          <span className={s.overlayTextVaulted}>Vaulted</span>
        </div>
      )}
      <div className={s.badges}>
        {product._isHolo && (
          <span className={`${s.badge} ${s.badgeHolo}`}>Holo</span>
        )}
        <SignTypeBadge
          label={SignType.toLabel(signedType)}
          signedType={signedType}
        />
      </div>
    </ProductThumbnail>
  );
}

CardImage.propTypes = {
  hasVariants: PropTypes.bool.isRequired,
  isSquare: PropTypes.bool.isRequired,
  isVaulted: PropTypes.bool,
  product: PropTypes.object.isRequired,
  soldOut: PropTypes.bool.isRequired,
};

function CardFooter({ unavailable, soldOut }) {
  const isXs = useMediaQuery("(max-width: 480px)");

  if (isXs) {
    return <div></div>;
  }

  if (unavailable) {
    return (
      <span
        className={clsx(soldOut ? s.statusLabelSoldOut : s.statusLabelVaulted)}
      >
        {soldOut ? "Sold Out" : "Vaulted"}
      </span>
    );
  }

  return (
    <span className={s.viewLink} role="presentation" aria-hidden="true">
      View details
    </span>
  );
}

CardFooter.propTypes = {
  unavailable: PropTypes.bool,
  soldOut: PropTypes.bool.isRequired,
  hasVariants: PropTypes.bool,
  numVariants: PropTypes.number,
};

export default function ProductCard({ product, onClick, to }) {
  const hasVariants = product._variants && product._variants.length > 1;
  const isSquare = product.aspect_ratio === 1;
  const { soldOut, isVaulted, unavailable } = getAvailability(product);

  const priceStr = hasVariants
    ? usd(product._priceRange.min)
    : usd(product._totalPrice);

  const ariaLabel = buildAriaLabel({
    name: product.name,
    priceStr,
    soldOut,
    isVaulted,
  });

  const Root = to ? Link : "button";
  const rootProps = to
    ? { to, className: s.card, "aria-label": ariaLabel }
    : {
        className: s.card,
        onClick: () => onClick(product),
        "aria-label": ariaLabel,
      };

  return (
    <Root {...rootProps}>
      <CardImage
        product={product}
        soldOut={soldOut}
        isVaulted={isVaulted}
        isSquare={isSquare}
      />
      <div className={s.body}>
        <div className={s.header}>
          <div>
            <div className={s.name}>{product.name}</div>
            <div className={s.type}>{product.product_type}</div>
          </div>
        </div>

        {product._vaNames && product._vaNames.length > 0 && (
          <div className={s.va}>
            Signed by{" "}
            {joinEnglishPick(F.identity, product._vaNames, { max: 2 })}
          </div>
        )}

        <div className={s.footer}>
          <CardFooter unavailable={unavailable} soldOut={soldOut} />

          {!unavailable && (
            <div className={s.price}>
              {hasVariants &&
                product._priceRange.min !== product._priceRange.max && (
                  <span className={s.priceRange}>From </span>
                )}
              {priceStr}
            </div>
          )}
        </div>
      </div>
    </Root>
  );
}

ProductCard.propTypes = {
  product: PropTypes.shape({
    product_id: PropTypes.number,
    name: PropTypes.string.isRequired,
    image_url: PropTypes.string,
    product_type: PropTypes.string,
    aspect_ratio: PropTypes.number,
    vaulted_until: PropTypes.string,
    signed_type: PropTypes.string,
    stock: PropTypes.number,
    _variants: PropTypes.array,
    _priceRange: PropTypes.shape({
      min: PropTypes.number,
      max: PropTypes.number,
    }),
    _totalPrice: PropTypes.number,
    _vaNames: PropTypes.arrayOf(PropTypes.string),
    _isHolo: PropTypes.bool,
  }).isRequired,
  onClick: PropTypes.func,
  to: PropTypes.string,
};
